/**
 * useStandUpRedirectHandlers
 * 
 * Custom hook encapsulating all event handlers for StandUpRedirectScreen
 * Manages:
 * - News feed overlay and news item operations
 * - Lighthouse task and card action plan creation
 * - Favorite toggle operations
 * - Navigation and screen transitions
 * - Form submission and standup lifecycle
 */

import { useCallback, Dispatch, SetStateAction, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import {
  checkIsSubmittedBtnClick,
  setStandupStatus,
  setToastNotification,
  clearTopOpportunities,
  setNews,
  STANDUP_API_NAMES,
  setIsSavedForLater,
  setIsSubmitted,
} from '../../../store/slices/standUpSlice';
import { clearToastNotification as clearActionPlanToastNotification } from '../../../store/slices/actionPlanSlice';
import { resetStandupRedirect } from '../../../store/slices/standupRedirectSlice';
import { createApiClient, apiClient, getTimezoneOffsetString } from '../../../api/apiClient';
import { getSessionLockService, initializeSessionLockService } from '../../../api/sessionLockService';
import { showSessionLockConflictModal, showLockExpirationWarning, hideLockExpirationWarning } from '../../../store/slices/sessionLockSlice';
import { handleStandUpErrorWithFriendlyMessage } from '../../../store/utils/standUpErrorHandler';
import logger from '../../../utils/logger';
import { NewsItem } from '../types';

/**
 * Parameters required by the hook
 */
export interface UseStandUpRedirectHandlersParams {
  // State setters
  setNewsFeedOverlayVisible: Dispatch<SetStateAction<boolean>>;
  setSessionNewsList: Dispatch<SetStateAction<NewsItem[]>>;
  setApiNewsList: Dispatch<SetStateAction<NewsItem[]>>;
  setStandupNewsList: Dispatch<SetStateAction<NewsItem[]>>;
  setGlobalNewsList: Dispatch<SetStateAction<NewsItem[]>>;
  setSelectedLighthouseTask: Dispatch<SetStateAction<any>>;
  setShowActionPlanScreen: Dispatch<SetStateAction<boolean>>;
  setSelectedLighthouseCard: Dispatch<SetStateAction<any>>;
  setActiveTab: Dispatch<SetStateAction<string>>;
  setIsBackToEditInViewMode: Dispatch<SetStateAction<boolean>>;
  setShowSaveForLaterModal: Dispatch<SetStateAction<boolean>>;
  setCompletedLighthouseTaskIds: Dispatch<SetStateAction<string[]>>;
  setAllLighthouseTasks: Dispatch<SetStateAction<any[]>>;
  setCompletedStandupStatus: Dispatch<SetStateAction<string>>;
  setFavoriteSparcCards: Dispatch<SetStateAction<string[]>>;
  setFavoriteLighthouseCards: Dispatch<SetStateAction<string[]>>;
  setFavoriteLighthouseTasks: Dispatch<SetStateAction<string[]>>;
  setNewsTitle: Dispatch<SetStateAction<string>>;
  setNewsDescription: Dispatch<SetStateAction<string>>;
  setSelectedOpportunity: Dispatch<SetStateAction<any>>;
  setSelectedActionPlanForView: Dispatch<SetStateAction<any>>;
  setNewlyCreatedStandupId: Dispatch<SetStateAction<string>>;
  setAllKpiCards: Dispatch<SetStateAction<any[]>>;
  setKeyActivitiesRefreshFn: Dispatch<SetStateAction<(() => void) | null>>;
  setNewsTitleError: Dispatch<SetStateAction<boolean>>;
  setNewsDescriptionError: Dispatch<SetStateAction<boolean>>;
  setShowTopOpportunityUnfavouriteModal: Dispatch<SetStateAction<boolean>>;
  setPendingUnfavouriteOpportunityId: Dispatch<SetStateAction<string | null>>;

  // UI state
  activeTab: string;
  pendingUnfavouriteOpportunityId: string | null;
  isViewMode: boolean;
  isEditMode: boolean;
  viewStandupData: any;
  newsTitle: string;
  newsDescription: string;
  selectedLighthouseTask: any;
  completedLighthouseTaskIds: string[];
  allLighthouseTasks: any[];
  keyActivitiesRefreshFn: (() => void) | null;
  apiNewsList: NewsItem[];

  // Loading states
  favoritesLoading: boolean;
  lighthouseExecutionLoading: boolean;
  kpiMetricsLoading: boolean;
  lighthouseTasksLoading: boolean;
  newsLoading: boolean;

  // Data
  standupSessionId: string;
  standupId: string;
  standupDate: string;
  sessionData: any;
  periodWeekData: any[];
  standupStatus: any;
  userEmail: string;
  selectedLocation: any;
  newlyCreatedStandupId?: string;
  topOpportunitiesFromRedux: any[];

  // Callbacks
  onBackPress?: () => void;
  submitStandUp: (params: any) => Promise<any>;
  updateStandup: (params: any) => Promise<any>;
  fetchTopOpportunitiesData: (page?: number, limit?: number, sessionId?: string , isTemp?: string) => Promise<any>;
  fetchStandUpData: () => Promise<void>;
  fetchNewsData: () => Promise<void>;
  refreshStandupNews: () => Promise<void>;
  handleToggleFavoriteOpportunity: (id: string, sessionId?: string) => Promise<void>;
}

/**
 * Return type for the hook
 */
export interface UseStandUpRedirectHandlersReturn {
  handleOpenNewsFeedOverlay: () => void;
  handleCloseNewsFeedOverlay: () => void;
  handleSaveNews: (newsList: Array<{ id: string; title: string; description: string; date: string; createdBy: string }>) => void;
  handleLighthouseTaskContinue: (task: any) => void;
  handleLighthouseCardActionPlan: (card: any) => void;
  handleSparcCardActionPlan: (card: any) => void;
  handleActionPlanBack: () => void;
  handleDeleteNews: (newsId: string) => void;
  deletingNewsId: string | null;
  handleBackToEditPress: () => void;
  backtoDashboardPress: () => void;
  areButtonsDisabledDueToLoading: () => boolean;
  handleBack: () => Promise<void>;
  handleSaveForLater: () => void;
  handleCancelSaveForLater: () => void;
  handleConfirmSaveForLater: () => Promise<void>;
  handleContinue: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  handleStandupCompletion: () => Promise<void>;
  handleActionPlanSubmit: (data: any) => Promise<void>;
  toggleFavoriteCard: (setFavorites: Dispatch<SetStateAction<string[]>>) => (cardId: string) => void;
  handleToggleFavorite: (cardId: string) => void;
  handleToggleLighthouseFavorite: (cardId: string) => void;
  handleToggleFavoriteLighthouseTask: (cardId: string) => void;
  handleViewOpportunityDetails: (opportunity: any) => void;
  handleAddNews: () => void;
  isAddingNewsLoading: boolean;
  handleBackFromDetails: () => void;
  handleActionPlanSaved: () => Promise<void>;
  handleViewActionPlanDetails: (actionPlan: any) => void;
  handleBackFromActionPlanDetails: () => void;
  handleKeyActivitiesRefresh: (refreshFn: () => void) => void;
  handleToggleFavoriteOpportunityWithSync: (id: string) => Promise<void>;
  handleAllKpiCardsChange: (cards: any[]) => void;
  handleBreadcrumbBack: () => Promise<void>;
  handleTopOpportunityToggleWithModal: (id: string) => Promise<void>;
  handleConfirmUnfavouriteOpportunity: () => Promise<void>;
  handleCancelUnfavouriteOpportunity: () => void;
}

/**
 * Hook that encapsulates all handler functions for StandUpRedirectScreen
 */
export const useStandUpRedirectHandlers = (
  params: UseStandUpRedirectHandlersParams
): UseStandUpRedirectHandlersReturn => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Local state for tracking which news item is being deleted
  const [deletingNewsId, setDeletingNewsId] = useState<string | null>(null);
  // Local state for tracking if news is being added
  const [isAddingNews, setIsAddingNews] = useState<boolean>(false);

  const {
    setNewsFeedOverlayVisible,
    setSessionNewsList,
    setApiNewsList,
    setStandupNewsList,
    setGlobalNewsList,
    setSelectedLighthouseTask,
    setShowActionPlanScreen,
    setSelectedLighthouseCard,
    setActiveTab,
    setIsBackToEditInViewMode,
    setShowSaveForLaterModal,
    setCompletedLighthouseTaskIds,
    setAllLighthouseTasks,
    setCompletedStandupStatus,
    setFavoriteSparcCards,
    setFavoriteLighthouseCards,
    setFavoriteLighthouseTasks,
    setNewsTitle,
    setNewsDescription,
    setSelectedOpportunity,
    setSelectedActionPlanForView,
    setNewlyCreatedStandupId,
    newlyCreatedStandupId,
    setAllKpiCards,
    setKeyActivitiesRefreshFn,
    setNewsTitleError,
    setNewsDescriptionError,
    setShowTopOpportunityUnfavouriteModal,
    setPendingUnfavouriteOpportunityId,
    activeTab,
    isViewMode,
    isEditMode,
    viewStandupData,
    newsTitle,
    newsDescription,
    selectedLighthouseTask,
    completedLighthouseTaskIds,
    allLighthouseTasks,
    keyActivitiesRefreshFn,
    apiNewsList,
    favoritesLoading,
    lighthouseExecutionLoading,
    kpiMetricsLoading,
    lighthouseTasksLoading,
    newsLoading,
    standupSessionId,
    standupId,
    standupDate,
    sessionData,
    periodWeekData,
    standupStatus,
    userEmail,
    selectedLocation,
    pendingUnfavouriteOpportunityId,
    topOpportunitiesFromRedux,
    onBackPress,
    submitStandUp,
    updateStandup,
    fetchTopOpportunitiesData,
    fetchStandUpData,
    fetchNewsData,
    refreshStandupNews,
    handleToggleFavoriteOpportunity,
  } = params;

  // News Feed Handlers
  const handleOpenNewsFeedOverlay = useCallback(() => {
    setNewsFeedOverlayVisible(true);
  }, [setNewsFeedOverlayVisible]);

  const handleCloseNewsFeedOverlay = useCallback(() => {
    setNewsFeedOverlayVisible(false);
  }, [setNewsFeedOverlayVisible]);

  const handleSaveNews = useCallback(
    (newsList: Array<{ id: string; title: string; description: string; date: string; createdBy: string }>) => {
      setSessionNewsList(prev => [...newsList, ...prev]);
    },
    [setSessionNewsList]
  );

  const handleDeleteNews = useCallback(
    async (newsId: string) => {
      // Check if this is a temporary news item (not yet saved to backend)
      // Temporary news IDs start with 'news-' prefix
      const isTemporaryNews = newsId.startsWith('news-');

      if (isTemporaryNews) {
        // For temporary news, just remove from session state without API call
        setSessionNewsList(prev => prev.filter(news => news.id !== newsId));
        return;
      }

      // For saved news, call DELETE API first
      setDeletingNewsId(newsId);
      try {
        const api = createApiClient();
        const response = await apiClient(api, `/stand-up/news?news_id=${newsId}`, 'DELETE');

        if (response?.status === 200 || response?.success) {
          // Immediately remove from all local state for instant UI update
          setSessionNewsList(prev => prev.filter(news => news.id !== newsId));
          setApiNewsList(prev => prev.filter(news => news.id !== newsId));
          setStandupNewsList(prev => prev.filter(news => news.id !== newsId));
          setGlobalNewsList(prev => prev.filter(news => news.id !== newsId));
          
          dispatch(setToastNotification({
            header: 'Success',
            subtext: 'News deleted successfully',
            variant: 'success',
            position: 'top',
            duration: 3000,
          }));
          
          // Add a small delay to allow the backend to fully commit the transaction
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Refresh news data from API to update all news lists (standupNewsList, globalNewsList, apiNewsList)
          await refreshStandupNews();
        } else {
          throw new Error('Failed to delete news');
        }
      } catch (error: any) {
        console.error('Error deleting news:', error);
        
        // Use centralized error handler to show overlay/modal for API failures
        try {
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.DELETE_NEWS,
            error,
            true, // displayModal = true → show overlay
            { newsId }
          );
        } catch (handlerErr) {
          console.error('Error while invoking standup error handler for delete news:', handlerErr);
        }
      } finally {
        setDeletingNewsId(null);
      }
    },
    [setSessionNewsList, setApiNewsList, setStandupNewsList, setGlobalNewsList, dispatch, refreshStandupNews]
  );

  // Lighthouse Task Handlers
  const handleLighthouseTaskContinue = useCallback(
    (task: any) => {
      const isContinuousImprovement = task.module_name === 'Continuous Improvement' || task.category === 'Continuous Improvement';

      const formattedTask = {
        ...task,
        id: task.id,
        module_name: task.category || task.module_name || '',
        title: task.title || '',
        kfa: task.description || task.kfa || '',
        kfa_id: task.kfa_id || task.id || '',
        startDate: sessionData.standupDate || standupDate || '',
        targetDate: sessionData.standupDate || standupDate || '',
        isContinuousImprovement,
      };

      console.log('[StandUpRedirectScreen] Creating action plan for lighthouse task:', formattedTask);
      // Clear card state before setting task to prevent state collision
      setSelectedLighthouseCard(null);
      setSelectedLighthouseTask(formattedTask);
      setShowActionPlanScreen(true);
    },
    [setSelectedLighthouseTask, setSelectedLighthouseCard, setShowActionPlanScreen, sessionData, standupDate]
  );

  // Lighthouse Card Handlers
  const handleLighthouseCardActionPlan = useCallback(
    (card: any) => {
      const formattedCard = {
        module_name: 'Lighthouse Execution',
        title: card.title || '',
        description: card.title || '',
        kfa: card.title || '',
        question_id: card.question_id, // Pass through question_id for action plan capabilities
        is_parent: card.is_parent ?? false, // Pass through is_parent for action plan capabilities
        startDate: sessionData.standupDate || standupDate || '',
        targetDate: sessionData.standupDate || standupDate || '',
        isLighthouseExecution: true,
      };

      console.log('[StandUpRedirectScreen] Creating action plan for Lighthouse Execution card:', formattedCard);
      // Clear task state before setting card to prevent state collision
      setSelectedLighthouseTask(null);
      setSelectedLighthouseCard(formattedCard);
      setShowActionPlanScreen(true);
    },
    [setSelectedLighthouseCard, setSelectedLighthouseTask, setShowActionPlanScreen, sessionData, standupDate]
  );

  const handleSparcCardActionPlan = useCallback(
    (card: any) => {
      const formattedCard = {
        module_name: 'SPARC KPI',
        title: card.title || '',
        description: card.title || '',
        kfa: card.title || '',
        card_id: card.id,
        startDate: sessionData.standupDate || standupDate || '',
        targetDate: sessionData.standupDate || standupDate || '',
        isSparcKpi: true,
        TYPE: 'KPI',
      };

      console.log('[StandUpRedirectScreen] Creating action plan for SPARC KPI card:', formattedCard);
      // Clear task state before setting card to prevent state collision
      setSelectedLighthouseTask(null);
      setSelectedLighthouseCard(formattedCard);
      setShowActionPlanScreen(true);
    },
    [setSelectedLighthouseCard, setSelectedLighthouseTask, setShowActionPlanScreen, sessionData, standupDate]
  );

  const handleActionPlanBack = useCallback(() => {
    setShowActionPlanScreen(false);
    setSelectedLighthouseTask(null);
    setSelectedLighthouseCard(null);
    
    // Clear actionPlan toast to prevent it showing in ActionPlan module later
    dispatch(clearActionPlanToastNotification());
  }, [setShowActionPlanScreen, setSelectedLighthouseTask, setSelectedLighthouseCard, dispatch]);

  // Navigation Handlers
  const handleBackToEditPress = useCallback(async () => {
    console.log('[StandUpRedirectHandlers] 🔵 Back to Edit clicked, acquiring lock...');
    try {
      dispatch(checkIsSubmittedBtnClick(false));
      // Get lock service
      let lockService = getSessionLockService();
      console.log('[StandUpRedirectHandlers] Lock service instance:', lockService ? 'EXISTS' : 'NULL');

      if (!lockService) {
        console.log('[StandUpRedirectHandlers] Creating API client and initializing lock service...');
        const api = createApiClient();
        lockService = initializeSessionLockService(api);
        console.log('[StandUpRedirectHandlers] Lock service initialized:', lockService ? 'SUCCESS' : 'FAILED');
      }

      if (!lockService) {
        console.error('[StandUpRedirectHandlers] ❌ CRITICAL: Lock service is still null after initialization');
        return;
      }

      // Set up callbacks for lock acquisition
      lockService.setExpirationWarningCallback((resourceId: string, lockData: any) => {
        console.log('[StandUpRedirectHandlers] 🟡 Expiration warning callback fired for:', resourceId);
        dispatch(
          showLockExpirationWarning({
            resourceId,
            expiresAt: lockData.expiresAt,
            lockToken: lockData.lockToken,
          }),
        );
      });

      // Set up callback for lock released (dismiss warning popup)
      lockService.setLockReleasedCallback((resourceId: string) => {
        console.log('[StandUpRedirectHandlers] Lock released, hiding warning popup for:', resourceId);
        dispatch(hideLockExpirationWarning());
      });

      lockService.setAcquisitionFailureCallback((error: any) => {
        console.error('[StandUpRedirectHandlers] ❌ Acquisition failure callback fired:', error);
        if (error?.response?.status === 423 || error?.lockConflict) {
          const conflictData = error.lockConflict || error.response?.data;
          dispatch(
            showSessionLockConflictModal({
              isLocked: conflictData.isLocked,
              alreadyLocked: conflictData.alreadyLocked,
              lockedBy: conflictData.lockedBy,
              lockedByName: conflictData.lockedByName,
              lockedAt: conflictData.lockedAt,
              expiresAt: conflictData.expiresAt,
              resourceType: 'Standup',
            }),
          );
        }
      });

      // Get standupData - could be from viewStandupData or need to construct from standupId
      const standupItem = viewStandupData || { id: standupId };
      
      if (!standupItem?.id) {
        console.error('[StandUpRedirectHandlers] ❌ Missing standup ID for lock acquisition');
        return;
      }

      console.log('[StandUpRedirectHandlers] Acquiring lock with ID:', standupItem.id.toString());
      const lockResponse = await lockService.acquireLock({
        module: 'STANDUP',
        submodule: 'STANDUP',
        id: standupItem.id.toString(),
        sessionId: '',
      });

      console.log('✅ [StandUpRedirectHandlers] LOCK ACQUIRED SUCCESSFULLY on back to edit:', {
        lockToken: lockResponse?.lockToken,
        expiresAt: lockResponse?.expiresAt,
        message: lockResponse?.message,
      });

      // Only switch to edit mode if lock acquisition is successful
      if (isViewMode) {
        setActiveTab('review-performance');
        setIsBackToEditInViewMode(true);
      } else {
        setActiveTab('review-progress');
        setIsBackToEditInViewMode(false);
      }

    } catch (error: any) {
      console.error('❌ [StandUpRedirectHandlers] EXCEPTION caught in handleBackToEditPress:', {
        message: error?.message,
        code: error?.code,
        statusCode: error?.response?.status,
        statusText: error?.response?.statusText,
        errorData: error?.response?.data,
        stack: error?.stack,
      });

      // If lock acquisition fails, do NOT redirect - stay in current view
      console.log('[StandUpRedirectHandlers] Lock acquisition failed - staying in current view');
    }
  }, [isViewMode, setActiveTab, setIsBackToEditInViewMode, dispatch, viewStandupData, standupId]);

  const backtoDashboardPress = useCallback(() => {
    dispatch(checkIsSubmittedBtnClick(false));
    dispatch(resetStandupRedirect());
    dispatch(clearTopOpportunities());
    if (onBackPress) {
      onBackPress();
      // Fetch fresh standup and news data after navigation
      fetchStandUpData?.();
      fetchNewsData?.();
      fetchTopOpportunitiesData?.();
    }
  }, [dispatch, onBackPress]);

  // Button Loading State Handler
  const areButtonsDisabledDueToLoading = useCallback((): boolean => {
    switch (activeTab) {
      case 'review-performance':
        return favoritesLoading || lighthouseExecutionLoading || kpiMetricsLoading;
      case 'key-activities':
        return favoritesLoading || lighthouseTasksLoading;
      case 'news':
        return newsLoading;
      case 'review-progress':
        return false;
      default:
        return false;
    }
  }, [activeTab, favoritesLoading, lighthouseExecutionLoading, kpiMetricsLoading, lighthouseTasksLoading, newsLoading]);

  // Breadcrumb Back Navigation Handler - Always calls discard API and goes to dashboard
  const handleBreadcrumbBack = useCallback(async () => {
    console.log('Breadcrumb back pressed - navigating to dashboard');
    
    // Always call discard API for both create and edit modes
    if (standupSessionId) {
      try {
        const api = await createApiClient();
        await apiClient(api, `/stand-up/discard?standup_session_id=${encodeURIComponent(standupSessionId)}`, 'DELETE');
        console.log('Discard API called successfully for session:', standupSessionId);
      } catch (error) {
        console.error('Error calling discard API:', error);
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.DISCARD_STANDUP,
          error,
          true,
          { standupSessionId, action: 'breadcrumb_back' }
        );
      }
    }
    
    // Navigate to dashboard
    dispatch(resetStandupRedirect());
    if (onBackPress) {
      onBackPress();
      // Fetch fresh standup and news data after navigation
      fetchStandUpData?.();
      fetchNewsData?.();
      fetchTopOpportunitiesData?.();
    }
  }, [standupSessionId, dispatch, onBackPress, fetchStandUpData, fetchNewsData, fetchTopOpportunitiesData]);

  // Back Navigation Handler
  const handleBack = useCallback(async () => {
    if (isViewMode && viewStandupData?.status?.toLowerCase() === 'completed') {
      console.log('Completed standup in view mode - navigating to dashboard');
      dispatch(resetStandupRedirect());
      if (onBackPress) {
        onBackPress();
        // Fetch fresh standup and news data after navigation
        fetchStandUpData?.();
        fetchNewsData?.();
        fetchTopOpportunitiesData?.();
      }
      return;
    }

    const steps = ['review-performance', 'key-activities', 'news', 'review-progress'];
    const currentIndex = steps.indexOf(activeTab);

    if (currentIndex === 0) {
      dispatch(clearTopOpportunities()); // Clear top opportunities to fetch fresh data on dashboard
      console.log('First step - navigating to dashboard');
      if (isEditMode && standupSessionId) {
        try {
          const api = await createApiClient();
          await apiClient(api, `/stand-up/discard?standup_session_id=${encodeURIComponent(standupSessionId)}`, 'DELETE');
          console.log('Discard API called for Edit Standup:', standupSessionId);
        } catch (error) {
          console.error('Error calling discard API (Edit Standup):', error);
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.DISCARD_STANDUP,
            error,
            true,
            { standupSessionId, isEditMode: true, action: 'back_from_edit' }
          );
        }
      } else if (!isEditMode && !isViewMode && standupSessionId) {
        try {
          const api = await createApiClient();
          await apiClient(api, `/stand-up/discard?standup_session_id=${encodeURIComponent(standupSessionId)}`, 'DELETE');
          console.log('Discard API called successfully for session:', standupSessionId);
        } catch (error) {
          console.error('Error calling discard API:', error);
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.DISCARD_STANDUP,
            error,
            true,
            { standupSessionId, action: 'back_from_new' }
          );
        }
      }
      dispatch(resetStandupRedirect());
      if (onBackPress) {
        onBackPress();
        // Fetch fresh standup and news data after navigation
        fetchStandUpData?.();
        fetchNewsData?.();
        fetchTopOpportunitiesData?.();
      }
    } else if (currentIndex > 0) {
      console.log(`Going back from step ${currentIndex + 1} to step ${currentIndex}`);
      setActiveTab(steps[currentIndex - 1]);
    }
  }, [isViewMode, viewStandupData, activeTab, isEditMode, isViewMode, standupSessionId, dispatch, onBackPress, setActiveTab, fetchStandUpData, fetchNewsData, fetchTopOpportunitiesData]);

  // Save for Later Handlers
  const handleSaveForLater = useCallback(() => {
    logger.log('Save for later - preparing modal', sessionData.standupDate);
    
    // Calculate the correct status NOW so Redux has the right value
    const scheduledDate = sessionData.standupDate || standupDate;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const scheduled = new Date(scheduledDate + 'T00:00:00');
    scheduled.setHours(0, 0, 0, 0);
    
    // For Save for Later: "In Progress" for today/past, "Saved" for future
    let calculatedStatus = 'In Progress';
    if (scheduled > today) {
      calculatedStatus = 'Saved';
    }
    
    console.log('[Save for Later] Calculated status:', calculatedStatus, 'for date:', scheduledDate);
    
    let setStandupStatusPayload = {
      isSaveForLater: true,
      date: sessionData.standupDate || '',
      status: calculatedStatus, // Add the calculated status to the payload
    };
    dispatch(setStandupStatus(setStandupStatusPayload));
    setShowSaveForLaterModal(true);
  }, [sessionData, standupDate, dispatch, setShowSaveForLaterModal]);

  const handleCancelSaveForLater = useCallback(() => {
    setShowSaveForLaterModal(false);
    dispatch(setStandupStatus(''));
  }, [setShowSaveForLaterModal, dispatch]);

  const handleConfirmSaveForLater = useCallback(async () => {
    console.log('========================================');
    console.log('[Save for Later] Confirm button clicked');
    console.log('[Save for Later] standupStatus from PARAMS:', standupStatus);
    console.log('========================================');
    
    // Calculate status based on scheduled date vs. current date
    const scheduledDate = sessionData.standupDate || standupDate;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Parse scheduled date in LOCAL timezone (not UTC) to avoid timezone offset issues
    const scheduled = new Date(scheduledDate + 'T00:00:00');
    scheduled.setHours(0, 0, 0, 0);
    console.log('scheduled data and today is ', scheduled, today);
    let status = 'In Progress';
    if (scheduled > today) {
      status = 'Saved';
    }
    
    console.log('[Save for Later] Date comparison:');
    console.log('  - Scheduled date string:', scheduledDate);
    console.log('  - Today:', today.toString());
    console.log('  - Today ISO:', today.toISOString());
    console.log('  - Scheduled:', scheduled.toString());
    console.log('  - Scheduled ISO:', scheduled.toISOString());
    console.log('  - scheduled > today:', scheduled > today);
    console.log('  - CALCULATED STATUS (local var):', status);
    console.log('  - standupStatus from PARAMS:', standupStatus);
    console.log('========================================');
    
    // Set flags: Save for Later button clicked
    dispatch(setIsSavedForLater(true));
    dispatch(setIsSubmitted(false));
    
    console.log('[Save for Later] Calling submitStandUp with CALCULATED status:', status);
    
    await submitStandUp({
      standupId,
      sessionData,
      periodWeekData,
      standupStatus: status, // Use calculated status
      isSaveForLater: true,
      isSavedForLater: true,
      isSubmitted: false,
      onSuccess: () => {
        dispatch(resetStandupRedirect());
        dispatch(clearTopOpportunities());
        if (onBackPress) {
          onBackPress();
          // Fetch fresh standup and news data after navigation
          fetchStandUpData?.();
          fetchNewsData?.();
        }
      },
    });
  }, [standupId, sessionData, periodWeekData, standupDate, standupStatus, submitStandUp, dispatch, onBackPress, fetchStandUpData, fetchNewsData, fetchTopOpportunitiesData]);

  // Continue Handler
  const handleContinue = useCallback(async () => {
    const steps = ['review-performance', 'key-activities', 'news', 'review-progress'];
    const currentIndex = steps.indexOf(activeTab);

    if (currentIndex < steps.length - 1) {
      setActiveTab(steps[currentIndex + 1]);
    } else {
      if (!isViewMode) {
        let setStandupStatusPayload = {
          isSaveForLater: false,
          date: sessionData.standupDate || '',
        };
        dispatch(setStandupStatus(setStandupStatusPayload));

        if (!standupId) {
          console.log('Standup submitted - calling submitStandUp');
          await submitStandUp({
            sessionData,
            periodWeekData,
            standupStatus,
            onSuccess: () => {
             dispatch(clearTopOpportunities()); // Clear top opportunities to fetch fresh data on dashboard
              if (onBackPress) {
                onBackPress();
                // Fetch fresh standup and news data after navigation
                fetchStandUpData?.();
                fetchNewsData?.();
              }
            },
          });
        } else {
          console.log('Standup updated - calling updateStandup with standupId:', standupId);
          await updateStandup({
            standupId,
            sessionData,
            standupStatus,
            onSuccess: () => {
              dispatch(clearTopOpportunities()); // Clear top opportunities to fetch fresh data on dashboard
              if (onBackPress) {
                onBackPress();
                // Fetch fresh standup and news data after navigation
                fetchStandUpData?.();
                fetchNewsData?.();
              }
            },
          });
        }
      } else {
        let setStandupStatusPayload = {
          isSaveForLater: false,
          date: sessionData.standupDate || '',
        };
        dispatch(setStandupStatus(setStandupStatusPayload));
      }
    }
  }, [activeTab, isViewMode, sessionData, standupId, periodWeekData, standupStatus, dispatch, submitStandUp, updateStandup, onBackPress, setActiveTab, fetchStandUpData, fetchNewsData]);

  // Submit Handler
  const handleSubmit = useCallback(async () => {
    console.log('Standup submitted - calling submitStandUp');
    const scheduledDate = sessionData.standupDate || standupDate;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const scheduled = new Date(scheduledDate + 'T00:00:00');
    scheduled.setHours(0, 0, 0, 0);
    let status = 'In Progress';
    if (scheduled > today) {
      status = 'Not Started';
    }
    const effectiveStandupId = standupId || newlyCreatedStandupId;
    console.log('Standup submitted - calling submitStandUp');
    // Set flags: Submit button clicked
    dispatch(setIsSubmitted(true));
    dispatch(setIsSavedForLater(false));
    let setStandupStatusPayload = {
      isSaveForLater: false,
      date: sessionData.standupDate || '',
    };
    dispatch(setStandupStatus(setStandupStatusPayload));
    const result = await submitStandUp({
      standupId: effectiveStandupId,
      sessionData,
      periodWeekData,
      standupStatus: status,
      isSavedForLater: false,
      isSubmitted: true,
      onSuccess: () => {
        dispatch(checkIsSubmittedBtnClick(true));
        setIsBackToEditInViewMode(false);
        // Fetch fresh standup and news data after submission
        fetchStandUpData?.();
        fetchNewsData?.();
      },
    });
    if (result?.data?.data?.id) {
      logger.log('Standup created with ID:', result?.data?.data?.id);
      setNewlyCreatedStandupId(String(result?.data?.data?.id));
    }
  }, [sessionData, standupDate, standupId, periodWeekData, dispatch, submitStandUp, setIsBackToEditInViewMode, setNewlyCreatedStandupId, fetchStandUpData, fetchNewsData]);

  // Standup Completion Handler
  const handleStandupCompletion = useCallback(async () => {
    const effectiveStandupId = (typeof newlyCreatedStandupId === 'string' && newlyCreatedStandupId) || standupId || '';
    console.log('Standup completed - calling updateStandup with standupId:', effectiveStandupId);
    // Set flags: Submit/Complete button clicked
    dispatch(setIsSubmitted(true));
    dispatch(setIsSavedForLater(false));
    await updateStandup({
      standupId: effectiveStandupId,
      sessionData,
      standupStatus: "Completed",
      isSavedForLater: false,
      isSubmitted: true,
      onSuccess: () => {
        setCompletedStandupStatus("Completed");
      },
    });
  }, [newlyCreatedStandupId, standupId, sessionData, updateStandup, setCompletedStandupStatus, dispatch]);

  // Action Plan Submission Handler
  const handleActionPlanSubmit = useCallback(
    async (data: any) => {
      try {
        console.log("Action plan submitted:", data);

        if (selectedLighthouseTask) {
          const taskId = selectedLighthouseTask.id ||
            `${selectedLighthouseTask.module_name}-${selectedLighthouseTask.title}`;

          console.log('[handleActionPlanSubmit] Marking lighthouse task as completed');
          console.log('  - Task ID:', taskId);
          setCompletedLighthouseTaskIds(prev => {
            const updated = [...prev, taskId];
            console.log('  - Updated completedLighthouseTaskIds:', updated);
            return updated;
          });

          setAllLighthouseTasks((prevTasks) =>
            prevTasks.filter(
              (task) =>
                !(
                  task.module_name === selectedLighthouseTask.module_name &&
                  task.title === selectedLighthouseTask.title
                ),
            ),
          );
        }

        console.log('Refreshing top opportunities and KeyActivitiesTab after action plan creation...');
        try {
          await fetchTopOpportunitiesData(1, 10, sessionData.sessionId, "no_need"); // Pass session_id to fetch action plans for this session
          console.log('Top opportunities refreshed successfully');

          if (keyActivitiesRefreshFn) {
            console.log('Refreshing KeyActivitiesTab TableSection...');
            keyActivitiesRefreshFn();
            console.log('KeyActivitiesTab TableSection refreshed successfully');
          }
        } catch (refreshError) {
          console.error('Error refreshing data:', refreshError);
        }

        setShowActionPlanScreen(false);
        setSelectedLighthouseTask(null);
        
        // Clear actionPlan toast after successful creation to prevent it showing in ActionPlan module
        dispatch(clearActionPlanToastNotification());
      } catch (error) {
        console.error("Error handling action plan submission:", error);
      }
    },
    [selectedLighthouseTask, setCompletedLighthouseTaskIds, setAllLighthouseTasks, fetchTopOpportunitiesData, keyActivitiesRefreshFn, setShowActionPlanScreen, setSelectedLighthouseTask, sessionData, dispatch]
  );

  // Favorite Toggle Handlers
  const toggleFavoriteCard = useCallback(
    (setFavorites: Dispatch<SetStateAction<string[]>>) => (cardId: string) => {
      setFavorites(prev => prev.includes(cardId) ? prev.filter(id => id !== cardId) : [...prev, cardId]);
    },
    []
  );

  const handleToggleFavorite = useCallback(
    (cardId: string) => {
      setFavoriteSparcCards(prev => prev.includes(cardId) ? prev.filter(id => id !== cardId) : [...prev, cardId]);
    },
    [setFavoriteSparcCards]
  );

  const handleToggleLighthouseFavorite = useCallback(
    (cardId: string) => {
      setFavoriteLighthouseCards(prev => prev.includes(cardId) ? prev.filter(id => id !== cardId) : [...prev, cardId]);
    },
    [setFavoriteLighthouseCards]
  );

  const handleToggleFavoriteLighthouseTask = useCallback(
    (cardId: string) => {
      setFavoriteLighthouseTasks(prev => prev.includes(cardId) ? prev.filter(id => id !== cardId) : [...prev, cardId]);
    },
    [setFavoriteLighthouseTasks]
  );

  // Opportunity Handlers
  const handleViewOpportunityDetails = useCallback(
    (opportunity: any) => {
      setSelectedOpportunity(opportunity);
    },
    [setSelectedOpportunity]
  );

  const handleBackFromDetails = useCallback(() => {
    setSelectedOpportunity(null);
  }, [setSelectedOpportunity]);

  const handleToggleFavoriteOpportunityWithSync = useCallback(
    async (id: string) => {
      console.log('========================================');
      console.log('handleToggleFavoriteOpportunityWithSync called');
      console.log('Action Plan ID:', id);
      console.log('standupSessionId:', standupSessionId);
      console.log('========================================');
      
      await handleToggleFavoriteOpportunity(id, standupSessionId);

      if (keyActivitiesRefreshFn) {
        console.log('Refreshing KeyActivitiesTab data after favorite toggle...');
        keyActivitiesRefreshFn();
      }
    },
    [handleToggleFavoriteOpportunity, keyActivitiesRefreshFn, standupSessionId]
  );

  // News Handlers - Add news specifically for this standup
  const handleAddNews = useCallback(async () => {
    // Validate and set error states
    const isTitleEmpty = !newsTitle.trim();
    const isDescriptionEmpty = !newsDescription.trim();
    
    setNewsTitleError(isTitleEmpty);
    setNewsDescriptionError(isDescriptionEmpty);

    if (isTitleEmpty || isDescriptionEmpty) {
      return;
    }

    // Set loading state
    setIsAddingNews(true);

    // Format current UTC date without timezone conversion
    const formatCurrentDate = (): string => {
      const now = new Date();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = now.getUTCDate();
      const month = now.getUTCMonth();
      const year = now.getUTCFullYear();
      return `${day} ${monthNames[month]} ${year}`;
    };

    const newNews: NewsItem = {
      id: `news-${Date.now()}`,
      title: newsTitle.trim(),
      description: newsDescription.trim(),
      date: formatCurrentDate(),
      createdBy: userEmail,
      is_favourite: true, // Auto-favorite for standup-specific news
      source: 'global',
    };

    try {
      const api = createApiClient();
      const clientTimezone = getTimezoneOffsetString();

      // Step 1: Create news as pure global (same as dashboard) - no session_id/standup_id
      const createResponse = await apiClient(api, `/stand-up/news?clientTimezone=${encodeURIComponent(clientTimezone)}`, 'POST', {
        title: newNews.title,
        description: newNews.description,
        location_name: selectedLocation?.displayText || '',
        location_id: selectedLocation?.id?.toString() || '',
        source: 'global', // Mark as global news
        is_favourite: false, // Create as unfavorited first
      });

      // Check if API response is successful
      if (createResponse?.success && createResponse?.data?.id) {
        const newsId = createResponse.data.id;
        console.log('✅ News created successfully, now linking to standup...');
        
        // Step 2: Link to standup by starring it (same as starring global news)
        const linkResponse = await apiClient(api, '/stand-up/news', 'PUT', {
          news_id: newsId,
          title: newNews.title,
          description: newNews.description,
          location_name: selectedLocation?.displayText || '',
          location_id: selectedLocation?.id?.toString() || '',
          is_active: true,
          is_favourite: true, // Star it to link with standup
          standup_id: standupId || undefined,
          session_id: standupSessionId || undefined,
        });

        if (linkResponse?.success) {
          console.log('✅ News linked to standup successfully');
          
          // Create the news item object with data from backend
          const createdNewsItem: NewsItem = {
            id: newsId.toString(),
            title: newNews.title,
            description: newNews.description,
            date: newNews.date,
            createdBy: newNews.createdBy,
            is_favourite: true,
            source: 'global',
            standup_id: standupId ? parseInt(standupId.toString()) : null,
            session_id: standupSessionId || null,
          };

          // Add to standupNewsList only (not to globalNewsList yet)
          // When unstarred, it will be moved to globalNewsList
          setStandupNewsList(prev => [createdNewsItem, ...prev]);
          setApiNewsList(prev => [createdNewsItem, ...prev]);
          
          // Clear form inputs immediately for good UX
          setNewsTitle('');
          setNewsDescription('');
          setNewsTitleError(false);
          setNewsDescriptionError(false);
        } else {
          console.error('❌ Failed to link news to standup:', linkResponse?.message);
          // News was created but not linked - still clear form
          setNewsTitle('');
          setNewsDescription('');
        }
      } else {
        // API returned error response (success: false)
        console.error('❌ News save failed - API returned error:', createResponse?.message);
        const errorMessage = createResponse?.message || 'Failed to save news';
        
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.ADD_NEWS,
          new Error(errorMessage),
          true, // displayModal = true → show overlay
          { title: newNews.title, locationId: selectedLocation?.id, action: 'add_news', success: false }
        );
      }
    } catch (error: any) {
      // Network or unexpected error
      console.error('❌ Error saving news:', error);

      let errorMessage = 'Failed to save news';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Use centralized error handler to show overlay/modal
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.ADD_NEWS,
        new Error(errorMessage),
        true, // displayModal = true → show overlay
        { title: newNews.title, locationId: selectedLocation?.id, action: 'add_news' }
      );
    } finally {
      // Clear loading state
      setIsAddingNews(false);
    }
  }, [newsTitle, newsDescription, userEmail, selectedLocation, standupSessionId, standupId, setSessionNewsList, setNewsTitle, setNewsDescription, setNewsTitleError, setNewsDescriptionError, dispatch, refreshStandupNews]);

  const handleActionPlanSaved = useCallback(async () => {
    console.log('Action plan saved, refreshing top opportunities data...');
    try {
      await fetchTopOpportunitiesData(1, 10, standupSessionId, "no_need");
    } catch (error: any) {
      console.error('Error refreshing top opportunities:', error);

      let errorMessage = 'Failed to refresh top opportunities';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch(
        setToastNotification({
          header: "Error",
          subtext: errorMessage,
          variant: "error",
          position: "top",
          duration: 6000,
        }),
      );
    }
  }, [fetchTopOpportunitiesData, dispatch, standupSessionId]);

  // Action Plan View Handlers
  const handleViewActionPlanDetails = useCallback(
    (actionPlan: any) => {
      console.log('View action plan details:', actionPlan);
      setSelectedActionPlanForView(actionPlan);
    },
    [setSelectedActionPlanForView]
  );

  const handleBackFromActionPlanDetails = useCallback(() => {
    setSelectedActionPlanForView(null);
  }, [setSelectedActionPlanForView]);

  // Key Activities Refresh Handler
  const handleKeyActivitiesRefresh = useCallback(
    (refreshFn: () => void) => {
      setKeyActivitiesRefreshFn(() => refreshFn);
    },
    [setKeyActivitiesRefreshFn]
  );

  // KPI Cards Change Handler
  const handleAllKpiCardsChange = useCallback(
    (cards: any[]) => {
      console.log('=== handleAllKpiCardsChange called ===');
      console.log('Received cards:', cards);
      console.log('Current mode - isEditMode:', isEditMode, 'isViewMode:', isViewMode);
      setAllKpiCards(cards);
    },
    [isEditMode, isViewMode, setAllKpiCards]
  );

  // Top Opportunities Modal Handlers
  const handleTopOpportunityToggleWithModal = useCallback(
    async (id: string) => {
      // Check if item is currently favorited
      const currentItem = topOpportunitiesFromRedux.find(
        (item: any) => String(item.ID) === id
      );
      if (!currentItem) return;

      const isFavorited = currentItem.TOP_OPPORTUNITY === 1 || currentItem.TOP_OPPORTUNITY === true;

      if (isFavorited) {
        // Show confirmation modal before unfavouriting
        setPendingUnfavouriteOpportunityId(id);
        setShowTopOpportunityUnfavouriteModal(true);
      } else {
        // Direct call to favourite
        await handleToggleFavoriteOpportunityWithSync(id);
      }
    },
    [topOpportunitiesFromRedux, setPendingUnfavouriteOpportunityId, setShowTopOpportunityUnfavouriteModal, handleToggleFavoriteOpportunityWithSync]
  );

  const handleConfirmUnfavouriteOpportunity = useCallback(
    async () => {
      if (!pendingUnfavouriteOpportunityId) return;

      await handleToggleFavoriteOpportunityWithSync(pendingUnfavouriteOpportunityId);

      // Close modal and reset
      setShowTopOpportunityUnfavouriteModal(false);
      setPendingUnfavouriteOpportunityId(null);
    },
    [pendingUnfavouriteOpportunityId, handleToggleFavoriteOpportunityWithSync, setShowTopOpportunityUnfavouriteModal, setPendingUnfavouriteOpportunityId]
  );

  const handleCancelUnfavouriteOpportunity = useCallback(
    () => {
      setShowTopOpportunityUnfavouriteModal(false);
      setPendingUnfavouriteOpportunityId(null);
    },
    [setShowTopOpportunityUnfavouriteModal, setPendingUnfavouriteOpportunityId]
  );

  return {
    handleOpenNewsFeedOverlay,
    handleCloseNewsFeedOverlay,
    handleSaveNews,
    handleLighthouseTaskContinue,
    handleLighthouseCardActionPlan,
    handleSparcCardActionPlan,
    handleActionPlanBack,
    handleDeleteNews,
    deletingNewsId,
    handleBackToEditPress,
    backtoDashboardPress,
    areButtonsDisabledDueToLoading,
    handleBack,
    handleSaveForLater,
    handleCancelSaveForLater,
    handleConfirmSaveForLater,
    handleContinue,
    handleSubmit,
    handleStandupCompletion,
    handleActionPlanSubmit,
    toggleFavoriteCard,
    handleToggleFavorite,
    handleToggleLighthouseFavorite,
    handleToggleFavoriteLighthouseTask,
    handleViewOpportunityDetails,
    handleAddNews,
    isAddingNewsLoading: isAddingNews,
    handleBackFromDetails,
    handleActionPlanSaved,
    handleViewActionPlanDetails,
    handleBackFromActionPlanDetails,
    handleKeyActivitiesRefresh,
    handleToggleFavoriteOpportunityWithSync,
    handleAllKpiCardsChange,
    handleBreadcrumbBack,
    handleTopOpportunityToggleWithModal,
    handleConfirmUnfavouriteOpportunity,
    handleCancelUnfavouriteOpportunity,
  };
};
